import { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * PMR Rebill card for the Admin Job Desktop.
 *
 * Lets an admin enter LCV/vendor labor + materials on a WO, computes ProCall
 * markup + total, and generates a PMR-ready PDF via `/api/jobs/:id/pmr-bill-pdf`.
 */
export default function PmrRebillCard({ jobId }) {
  const [state, setState] = useState(null); // { invoice, profile, totals }
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [candidates, setCandidates] = useState(null); // { candidates, unscanned_count, non_material_count }
  const [candBusy, setCandBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    if (!jobId) return;
    api.get(`/jobs/${jobId}/pmr-invoice`).then(r => setState(r.data))
      .catch(() => {/* silent, empty */});
  }, [jobId]);

  const openPicker = async () => {
    if (dirty) { toast.error("Save first — you have unsaved changes"); return; }
    setPickerOpen(true);
    setCandBusy(true);
    setSelected(new Set());
    try {
      const r = await api.get(`/jobs/${jobId}/pmr-invoice/receipt-candidates`);
      setCandidates(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load receipts");
      setCandidates({ candidates: [], unscanned_count: 0, non_material_count: 0 });
    } finally { setCandBusy(false); }
  };

  const togglePick = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const confirmPull = async () => {
    if (selected.size === 0) { toast.error("Pick at least one receipt"); return; }
    setCandBusy(true);
    try {
      const r = await api.post(`/jobs/${jobId}/pmr-invoice/pull-receipts`, {
        receipt_ids: Array.from(selected),
      });
      setState({ invoice: r.data.invoice, profile: r.data.profile, totals: r.data.totals });
      toast.success(`Added ${r.data.added} receipt${r.data.added === 1 ? "" : "s"} to Materials`
        + (r.data.skipped ? ` · skipped ${r.data.skipped}` : ""));
      setPickerOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Pull failed");
    } finally { setCandBusy(false); }
  };

  const inv = state?.invoice || {
    vendor_name: "LCV", invoice_number: "", invoice_date: "",
    invoice_link: "", labor: [], materials: [], notes: "",
    flat_total_cents: null,
  };
  const totals = state?.totals || null;
  const profile = state?.profile || null;

  const upd = (k, v) => {
    setState(s => ({ ...(s || {}), invoice: { ...inv, [k]: v } }));
    setDirty(true);
  };

  const setLabor = (rows) => upd("labor", rows);
  const setMaterials = (rows) => upd("materials", rows);

  const addLabor = () => setLabor([...(inv.labor || []),
    { category: "other", description: "", cost_cents: 0 }]);
  const addMat = () => setMaterials([...(inv.materials || []),
    { description: "", cost_cents: 0 }]);

  const rmLabor = (idx) => setLabor(inv.labor.filter((_, i) => i !== idx));
  const rmMat = (idx) => setMaterials(inv.materials.filter((_, i) => i !== idx));

  const patchLabor = (idx, patch) => setLabor(inv.labor.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const patchMat = (idx, patch) => setMaterials(inv.materials.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const meta = profile?._effective_meta;
  const overrideKeys = meta?.override_keys || [];

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        vendor_name: inv.vendor_name || "LCV",
        invoice_number: inv.invoice_number || "",
        invoice_date: inv.invoice_date || "",
        invoice_link: inv.invoice_link || "",
        labor: (inv.labor || []).map(r => ({
          category: ["paint", "plumbing", "electrical", "hvac", "other"].includes(r.category) ? r.category : "other",
          description: r.description || "",
          cost_cents: Math.round(Number(r.cost_dollars ?? (r.cost_cents / 100)) * 100),
        })),
        materials: (inv.materials || []).map(r => ({
          description: r.description || "",
          cost_cents: Math.round(Number(r.cost_dollars ?? (r.cost_cents / 100)) * 100),
        })),
        notes: inv.notes || "",
        flat_total_cents: inv.flat_total_dollars
          ? Math.round(Number(inv.flat_total_dollars) * 100)
          : (inv.flat_total_cents || null),
      };
      const r = await api.put(`/jobs/${jobId}/pmr-invoice`, payload);
      setState(r.data);
      setDirty(false);
      toast.success("Rebill inputs saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const openPdf = async (detail = false) => {
    if (dirty) { toast.error("Save first — you have unsaved changes"); return; }
    if (!totals || totals.billed_total_cents === 0) {
      toast.error("Add labor or materials first");
      return;
    }
    try {
      const r = await api.get(`/jobs/${jobId}/pmr-bill-pdf`, {
        responseType: "blob",
        params: detail ? { detail: true } : {},
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "PDF failed");
    }
  };

  const d = (c) => `$${((c || 0) / 100).toFixed(2)}`;

  const importedReceiptIds = useMemo(() => new Set(
    (inv.materials || [])
      .map(m => m.source_receipt_id)
      .filter(Boolean)
  ), [inv.materials]);

  const withDollars = useMemo(() => ({
    labor: (inv.labor || []).map(r => ({ ...r, cost_dollars: r.cost_dollars ?? (r.cost_cents / 100) })),
    materials: (inv.materials || []).map(r => ({ ...r, cost_dollars: r.cost_dollars ?? (r.cost_cents / 100) })),
  }), [inv.labor, inv.materials]);

  return (
    <div className="bg-white border-2 border-[#0f172a] rounded-xl p-4 sm:p-5 shadow-[3px_3px_0px_#0f172a] mb-4"
      data-testid="pmr-rebill-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Rebill</div>
          <h3 className="font-display font-black text-lg text-[#0B1F3A]">PMR Rebill</h3>
          {profile && (
            <div className="text-[10px] text-slate-500 mt-0.5">
              Markup: paint {profile.paint_labor_pct}% · plumbing {profile.plumbing_labor_pct}% · electrical {profile.electrical_labor_pct}% · hvac {profile.hvac_labor_pct}% · other {profile.other_labor_pct}% · materials {profile.materials_pct}%
              {profile.min_charge_cents ? ` · min ${d(profile.min_charge_cents)}` : ""}
            </div>
          )}
          {meta?.portfolio_name && overrideKeys.length > 0 && (
            <div className="text-[10px] mt-1 inline-flex items-center gap-1 bg-cyan-500 text-white px-2 py-0.5 rounded font-black uppercase tracking-widest"
              data-testid="pmr-override-badge">
              🏢 {meta.portfolio_name} override · {overrideKeys.join(", ")}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <button onClick={save} disabled={busy || !dirty}
            data-testid="pmr-save-btn"
            className="h-8 px-3 border-2 border-[#0f172a] bg-white hover:bg-slate-100 text-slate-900 text-[10px] font-black uppercase tracking-widest disabled:opacity-40">
            {busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
          <button onClick={() => openPdf(false)} disabled={busy || dirty || !totals?.billed_total_cents}
            data-testid="pmr-pdf-btn"
            title="Client-safe view — no vendor cost, no markup shown"
            className="h-9 px-4 border-2 border-[#0f172a] bg-[#00C4CC] hover:bg-[#00a8af] text-[#0f172a] hover:text-white text-[11px] font-black uppercase tracking-widest disabled:opacity-40 shadow-[2px_2px_0px_#0f172a]">
            📄 Generate PMR Bill
          </button>
          <button onClick={() => openPdf(true)} disabled={busy || dirty || !totals?.billed_total_cents}
            data-testid="pmr-pdf-detail-btn"
            title="Internal detail — shows vendor cost + markup breakdown"
            className="h-7 px-3 border-2 border-slate-400 bg-white hover:bg-slate-100 text-slate-700 text-[9px] font-black uppercase tracking-widest disabled:opacity-40">
            🔒 Internal Detail
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <Field lbl="Vendor">
          <input value={inv.vendor_name || ""} onChange={e => upd("vendor_name", e.target.value)}
            data-testid="pmr-vendor" className={inputCls}/>
        </Field>
        <Field lbl="Vendor invoice #">
          <input value={inv.invoice_number || ""} onChange={e => upd("invoice_number", e.target.value)}
            data-testid="pmr-inv-num" className={inputCls}/>
        </Field>
        <Field lbl="Invoice date">
          <input type="date" value={inv.invoice_date || ""} onChange={e => upd("invoice_date", e.target.value)}
            data-testid="pmr-inv-date" className={inputCls}/>
        </Field>
        <Field lbl="Vendor invoice link (Drive / Dropbox / URL)">
          <input value={inv.invoice_link || ""} onChange={e => upd("invoice_link", e.target.value)}
            placeholder="https://…"
            data-testid="pmr-inv-link" className={inputCls}/>
        </Field>
      </div>

      {/* Labor */}
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-600">Labor</div>
          <button onClick={addLabor} data-testid="pmr-add-labor"
            className="text-[10px] font-black uppercase tracking-widest text-cyan-700 hover:underline">
            + Add labor line
          </button>
        </div>
        {withDollars.labor.length === 0 && (
          <div className="text-[11px] text-slate-400 italic mb-2">No labor lines yet.</div>
        )}
        {withDollars.labor.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-1.5 mb-1.5 items-center">
            <select value={r.category} onChange={e => patchLabor(i, { category: e.target.value })}
              data-testid={`pmr-labor-cat-${i}`}
              className="col-span-3 border-2 border-slate-300 rounded px-1.5 py-1 text-xs">
              <option value="paint">Paint</option>
              <option value="plumbing">Plumbing</option>
              <option value="electrical">Electrical</option>
              <option value="hvac">HVAC</option>
              <option value="other">Other</option>
            </select>
            <input value={r.description} onChange={e => patchLabor(i, { description: e.target.value })}
              placeholder="Description"
              data-testid={`pmr-labor-desc-${i}`}
              className="col-span-6 border-2 border-slate-300 rounded px-1.5 py-1 text-xs"/>
            <input type="number" step="0.01" min="0" value={r.cost_dollars || 0}
              onChange={e => patchLabor(i, { cost_dollars: e.target.value, cost_cents: Math.round(Number(e.target.value) * 100) })}
              placeholder="$"
              data-testid={`pmr-labor-cost-${i}`}
              className="col-span-2 border-2 border-slate-300 rounded px-1.5 py-1 text-xs font-mono text-right"/>
            <button onClick={() => rmLabor(i)}
              data-testid={`pmr-labor-rm-${i}`}
              className="col-span-1 text-red-600 text-lg font-black hover:bg-red-50 rounded" title="Remove">
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Materials */}
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-600">Materials</div>
          <div className="flex items-center gap-3">
            <button onClick={openPicker} data-testid="pmr-pull-receipts"
              title="Pull already-scanned receipts (Home Depot, Ferguson, etc.) from this WO"
              className="text-[10px] font-black uppercase tracking-widest text-fuchsia-700 hover:underline">
              📎 Pull from WO receipts
            </button>
            <button onClick={addMat} data-testid="pmr-add-mat"
              className="text-[10px] font-black uppercase tracking-widest text-cyan-700 hover:underline">
              + Add material line
            </button>
          </div>
        </div>
        {withDollars.materials.length === 0 && (
          <div className="text-[11px] text-slate-400 italic mb-2">No material lines yet.</div>
        )}
        {withDollars.materials.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-1.5 mb-1.5 items-center">
            <input value={r.description} onChange={e => patchMat(i, { description: e.target.value })}
              placeholder="Description"
              data-testid={`pmr-mat-desc-${i}`}
              className="col-span-9 border-2 border-slate-300 rounded px-1.5 py-1 text-xs"/>
            <input type="number" step="0.01" min="0" value={r.cost_dollars || 0}
              onChange={e => patchMat(i, { cost_dollars: e.target.value, cost_cents: Math.round(Number(e.target.value) * 100) })}
              placeholder="$"
              data-testid={`pmr-mat-cost-${i}`}
              className="col-span-2 border-2 border-slate-300 rounded px-1.5 py-1 text-xs font-mono text-right"/>
            <button onClick={() => rmMat(i)}
              data-testid={`pmr-mat-rm-${i}`}
              className="col-span-1 text-red-600 text-lg font-black hover:bg-red-50 rounded" title="Remove">
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Flat override */}
      <div className="mt-3 flex flex-col sm:flex-row sm:items-end gap-3">
        <Field lbl="Flat total override (dollars — overrides % markup)">
          <input type="number" step="0.01" min="0"
            value={inv.flat_total_dollars ?? (inv.flat_total_cents ? inv.flat_total_cents / 100 : "")}
            onChange={e => upd("flat_total_dollars", e.target.value)}
            placeholder="e.g., 250.00"
            data-testid="pmr-flat"
            className={`${inputCls} sm:w-48`}/>
        </Field>
        <Field lbl="Notes on PDF">
          <input value={inv.notes || ""} onChange={e => upd("notes", e.target.value)}
            data-testid="pmr-notes" className={inputCls}/>
        </Field>
      </div>

      {/* Totals preview */}
      {totals && (
        <div className="mt-4 pt-3 border-t-2 border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <StatBox lbl="Vendor sub" val={d(totals.vendor_subtotal_cents)} testid="pmr-t-vendor"/>
          <StatBox lbl="ProCall markup" val={d(totals.markup_subtotal_cents)} testid="pmr-t-markup"/>
          <StatBox lbl={totals.flat_used ? "Flat total" : "Computed"} val={d(totals.computed_total_cents)} testid="pmr-t-computed"/>
          <StatBox lbl="PMR bill" val={d(totals.billed_total_cents)} accent testid="pmr-t-billed"/>
        </div>
      )}
      {dirty && (
        <div className="mt-2 text-[10px] text-amber-700 font-black uppercase tracking-widest">
          Unsaved changes — tap Save before generating the PDF
        </div>
      )}

      {pickerOpen && (
        <ReceiptPicker
          candidates={candidates}
          busy={candBusy}
          selected={selected}
          onToggle={togglePick}
          onConfirm={confirmPull}
          onClose={() => setPickerOpen(false)}
          d={d}
        />
      )}
    </div>
  );
}

function ReceiptPicker({ candidates, busy, selected, onToggle, onConfirm, onClose, d }) {
  const rows = candidates?.candidates || [];
  const pickableCount = rows.filter(r => !r.already_imported).length;
  const selectedTotal = rows
    .filter(r => selected.has(r.id))
    .reduce((sum, r) => sum + (r.amount_cents || 0), 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3"
      onClick={onClose} data-testid="pmr-receipts-picker">
      <div className="bg-white border-2 border-[#0f172a] rounded-xl shadow-[6px_6px_0px_#0f172a] max-w-2xl w-full max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-slate-100 flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Pull materials from</div>
            <h3 className="font-display font-black text-lg text-[#0B1F3A]">WO Receipts</h3>
          </div>
          <button onClick={onClose} data-testid="pmr-picker-close"
            className="text-slate-400 hover:text-slate-700 text-2xl leading-none font-black">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {busy && !candidates && (
            <div className="text-sm text-slate-400 italic p-6 text-center">Loading receipts…</div>
          )}
          {!busy && rows.length === 0 && (
            <div className="text-sm text-slate-500 p-6 text-center">
              <div className="text-3xl mb-2">📭</div>
              <div className="font-bold mb-1">No materials receipts scanned yet on this WO.</div>
              <div className="text-xs text-slate-400">
                {candidates?.unscanned_count > 0 && (
                  <>Unscanned receipts on this WO: <b>{candidates.unscanned_count}</b>. Scan them from the Receipts tab first, then come back.</>
                )}
                {candidates?.non_material_count > 0 && (
                  <div className="mt-1">
                    Scanned but not tagged as materials: <b>{candidates.non_material_count}</b>. Only receipts flagged "materials / tools / hardware" appear here.
                  </div>
                )}
              </div>
            </div>
          )}
          {rows.map(r => {
            const disabled = r.already_imported;
            const isPicked = selected.has(r.id);
            return (
              <label key={r.id}
                data-testid={`pmr-receipt-${r.id}`}
                className={`flex items-center gap-3 p-2.5 mb-1.5 rounded-lg border-2 transition-all ${
                  disabled ? "opacity-40 border-slate-200 cursor-not-allowed" :
                  isPicked ? "border-[#0f172a] bg-cyan-50 cursor-pointer" :
                  "border-slate-200 hover:border-slate-400 cursor-pointer"
                }`}>
                <input type="checkbox" checked={isPicked} disabled={disabled}
                  onChange={() => onToggle(r.id)}
                  data-testid={`pmr-receipt-check-${r.id}`}
                  className="h-4 w-4 accent-cyan-600 flex-shrink-0"/>
                {r.thumb_url && (
                  <img src={r.thumb_url} alt="receipt" className="h-12 w-12 object-cover rounded border border-slate-200 flex-shrink-0"/>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-slate-900 truncate">
                    {r.vendor || "Unknown vendor"}
                    {disabled && <span className="ml-2 text-[10px] font-black uppercase tracking-widest text-emerald-600">✓ Already imported</span>}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {r.date || "—"} · {r.category || "materials"}
                    {r.notes ? ` · ${r.notes}` : ""}
                  </div>
                </div>
                <div className="font-mono font-black text-sm text-slate-900 flex-shrink-0">
                  {d(r.amount_cents)}
                </div>
              </label>
            );
          })}
        </div>

        <div className="p-4 border-t-2 border-slate-100 flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs text-slate-500">
            {selected.size > 0 ? (
              <><b className="text-slate-900">{selected.size}</b> selected · <b className="text-slate-900 font-mono">{d(selectedTotal)}</b></>
            ) : pickableCount > 0 ? (
              <>{pickableCount} available</>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              data-testid="pmr-picker-cancel"
              className="h-9 px-3 border-2 border-slate-300 text-slate-700 hover:bg-slate-50 text-[11px] font-black uppercase tracking-widest">
              Cancel
            </button>
            <button onClick={onConfirm} disabled={busy || selected.size === 0}
              data-testid="pmr-picker-confirm"
              className="h-9 px-4 border-2 border-[#0f172a] bg-[#00C4CC] hover:bg-[#00a8af] text-[#0f172a] hover:text-white text-[11px] font-black uppercase tracking-widest disabled:opacity-40 shadow-[2px_2px_0px_#0f172a]">
              {busy ? "Adding…" : `Add ${selected.size || ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full border-2 border-slate-300 rounded px-2 py-1.5 text-sm";
function Field({ lbl, children }) {
  return (
    <label className="block flex-1">
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">{lbl}</div>
      {children}
    </label>
  );
}
function StatBox({ lbl, val, accent, testid }) {
  return (
    <div className={`p-2 rounded-lg border-2 ${accent ? "border-[#0f172a] bg-[#00C4CC] text-[#0f172a]" : "border-slate-200 bg-slate-50"}`}
      data-testid={testid}>
      <div className={`text-[9px] font-black uppercase tracking-widest ${accent ? "text-[#0f172a]" : "text-slate-500"}`}>{lbl}</div>
      <div className="font-mono font-black text-sm">{val}</div>
    </div>
  );
}
